import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Menu, X, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'motion/react';

export const PublicHeader = () => {
  const { node, brand, userThemePref, setThemePref, activeBrandId } = useTenant();
  const { isSignedIn, signOut } = useCurrentUser();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const brandName = brand.name;
  const theme = 'bauhaus';

  const s = {
    nav: 'bg-white dark:bg-black border-b-[8px] md:border-b-[12px] border-black dark:border-white',
    text: 'text-black dark:text-white uppercase font-black tracking-widest text-sm hover:text-[#0044FF] dark:hover:text-[#FFD600] transition-colors',
    logo: 'text-black dark:text-white uppercase font-black tracking-tighter text-2xl md:text-3xl',
    mobileMenu: 'bg-white dark:bg-black border-black dark:border-white text-black dark:text-white font-black uppercase'
  };
  
  const navLinks = [
    { to: '/features', label: 'Features' },
    { to: '/use-cases', label: 'Use Cases' },
    { to: '/plans', label: 'Plans' },
    { to: '/tools', label: 'Tools', badge: 'FREE' },
    { to: '/compare', label: 'Compare' }
  ];

  return (
    <>
      <nav className={cn("fixed top-0 left-0 right-0 z-50 h-20 transition-all duration-500 flex items-center px-4", s.nav)}>
        <div className="w-full flex items-center justify-between relative z-10 px-2 md:px-8">
          <Link to="/" className={cn("text-xl font-display flex items-center gap-3", s.logo)}>
            {brand.logoUrl || node?.config?.logoUrl ? (
              <img src={brand.logoUrl || node?.config?.logoUrl} alt={brandName} className="h-8 w-8 object-contain bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
            ) : (
              <Zap className="fill-current" size={24} style={{ color: brand.primary }} />
            )}
            <span>
              {brandName}
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8 font-medium text-sm">
            {navLinks.map((nav) => {
              const isActive = location.pathname === nav.to;
              return (
                <Link 
                  key={nav.to}
                  to={nav.to} 
                  className={cn(
                    "transition-colors relative flex items-center gap-1.5", 
                    s.text,
                    isActive && "text-[#0044FF] dark:text-[#FFD600]"
                  )}
                >
                  <span className={cn(
                    "transition-colors duration-200",
                    isActive ? "font-bold" : "font-medium"
                  )}>
                    {nav.label}
                  </span>
                  {nav.badge && (
                    <span className="px-1.5 py-0.5 bg-[#FFD600] text-black text-[9px] font-black tracking-wider border border-black shadow-[1px_1px_0px_#000] rounded-none animate-pulse">
                      {nav.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          
          <div className="flex items-center gap-2 md:gap-3">
            {!isSignedIn ? (
              <>
                <Link 
                  to="/sign-in" 
                  className={cn(
                    "hidden sm:inline-flex items-center justify-center px-4 py-2 rounded-none font-bold font-mono transition-all duration-150 border-2 border-transparent hover:border-black dark:hover:border-white", 
                    s.text
                  )}
                >
                  Sign In
                </Link>
                <Link 
                  to="/sign-up" 
                  className="hidden sm:inline-flex items-center justify-center rounded-none font-mono font-black uppercase tracking-wider px-6 py-3 text-xs bg-black text-white dark:bg-white dark:text-black border-[2px] border-black dark:border-white shadow-[4px_4px_0px_#0044FF] dark:shadow-[4px_4px_0px_#FFD600] hover:bg-[#0044FF] hover:text-white dark:hover:bg-[#FFD600] dark:hover:text-black transition-all hover:shadow-[2px_2px_0px_#000] hover:translate-x-0.5 hover:translate-y-0.5"
                >
                  Create Free Org
                </Link>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-3">
                <Link 
                  to="/dashboard"
                  className={cn(
                    "inline-flex items-center justify-center px-4 py-2 rounded-none font-bold font-mono transition-all duration-150 border-2 border-transparent hover:border-black dark:hover:border-white",
                    s.text
                  )}
                >
                  Dashboard
                </Link>
                <Button 
                  onClick={() => signOut()}
                  className="rounded-custom px-6"
                >
                  Sign Out
                </Button>
              </div>
            )}

            {/* Theme Toggle */}
            <button
              onClick={() => {
                if (userThemePref === 'light') setThemePref('dark');
                else if (userThemePref === 'dark') setThemePref('system');
                else setThemePref('light');
              }}
              className={cn("p-2 rounded-none transition-colors hover:bg-black/10", s.text)}
              title={`Theme: ${userThemePref}`}
            >
              {userThemePref === 'light' ? (
                <Sun size={20} />
              ) : userThemePref === 'dark' ? (
                <Moon size={20} />
              ) : (
                <Monitor size={20} />
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button 
              className={cn("md:hidden p-2 rounded-none hover:bg-black/10", s.text)} 
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn("fixed inset-0 z-[100] flex flex-col items-center justify-center p-6 border-l", s.mobileMenu)}
          >
            <button 
              className="absolute top-6 right-6 p-2 rounded-none hover:bg-black/10"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X size={28} />
            </button>
            <div className="flex flex-col gap-6 text-2xl font-bold text-center w-full max-w-xs">
              {navLinks.map((nav) => (
                <Link 
                  key={nav.to} 
                  to={nav.to} 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="py-4 border-b border-current/10 flex items-center justify-center gap-2"
                >
                  <span>{nav.label}</span>
                  {nav.badge && (
                    <span className="px-2 py-0.5 bg-[#FFD600] text-black text-xs font-black tracking-wider border-2 border-black shadow-[2px_2px_0px_#000] rounded-none">
                      {nav.badge}
                    </span>
                  )}
                </Link>
              ))}
              
              <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-current/20">
                {!isSignedIn ? (
                  <>
                    <Link 
                      to="/sign-in" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn("w-full h-14 text-xl inline-flex items-center justify-center rounded-none font-bold font-mono transition-all duration-150 border-2 border-transparent hover:border-black dark:hover:border-white", s.text)}
                    >
                      Sign In
                    </Link>
                    <Link 
                      to="/sign-up" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full h-14 text-xl inline-flex items-center justify-center font-mono rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#0044FF] dark:shadow-[4px_4px_0px_#FFD600] font-black uppercase tracking-wider bg-black text-white dark:bg-white dark:text-black hover:bg-[#0044FF] hover:text-white transition-all"
                    >
                      CREATE FREE ORG
                    </Link>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/dashboard" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn("w-full h-14 text-xl inline-flex items-center justify-center rounded-none font-bold font-mono transition-all duration-150", s.text)}
                    >
                      Dashboard
                    </Link>
                    <Button onClick={() => { signOut(); setIsMobileMenuOpen(false); }} className="w-full h-14 text-xl rounded-custom">
                      Sign Out
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

